import { CATEGORY_TAX_MAP, INCOME_CATEGORY_TAX_MAP } from './constants';
import { categoryLabel, normalizeCategory } from './categoryHelpers';

const MEALS_CAT = 'Meals & Entertainment (50%)';
const NON_DEDUCTIBLE = new Set(['Non-deductible (Personal)', 'Non-deductible']);
const UNCATEGORIZED_CAT = 'Uncategorized';

function fmtDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 16).replace('T', ' ');
}

function deductibleAmt(irsCategory, total) {
  if (NON_DEDUCTIBLE.has(irsCategory) || irsCategory === UNCATEGORIZED_CAT) return 0;
  return irsCategory === MEALS_CAT ? total * 0.5 : total;
}

export async function exportTaxXlsx({
  expenseItems = [],
  incomeItems = [],
  groups = [],       // [[irsCategory, { items, total }], ...]
  incomeGroups = [], // [[category, { items, total }], ...]
  deductible = 0,
  businessIncome = 0,
  netProfit = 0,
  reportCurrency = 'USD',
  filename = 'tax-report',
  dateRange = 'All time',
  isCombined = false,
} = {}) {
  return new Promise((resolve, reject) => {
    const doExport = () => {
      try {
        const XLSX = window.XLSX;

        // ── Sheet 1: Tax Summary ───────────────────────────────────────────
        const summaryRows = [
          ['Tax Report Summary'],
          [],
          ['Date Range',      dateRange],
          ['Generated',       new Date().toISOString().slice(0, 10)],
          ['Report Currency', reportCurrency],
          [],
        ];

        if (incomeGroups.length > 0) {
          summaryRows.push(['Business Income']);
          summaryRows.push(['Category', `Amount (${reportCurrency})`, '# Items']);
          incomeGroups.forEach(([cat, { total, items }]) => {
            summaryRows.push([cat, total, items.length]);
          });
          summaryRows.push(['Total Business Income', businessIncome]);
          summaryRows.push([]);
        }

        summaryRows.push(['Business Expenses by IRS Category']);
        summaryRows.push(['IRS Tax Category', `Gross (${reportCurrency})`, `Deductible (${reportCurrency})`, '# Items']);
        groups.forEach(([cat, { total, items }]) => {
          const ded = deductibleAmt(cat, total);
          summaryRows.push([cat, total, ded, items.length]);
        });
        summaryRows.push(['Total Deductible', '', deductible]);
        summaryRows.push([]);

        if (incomeGroups.length > 0) {
          summaryRows.push(['Net Business Profit', netProfit]);
          summaryRows.push(['  (Business Income − Deductible Expenses)']);
        }

        const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
        wsSummary['!cols'] = [{ wch: 36 }, { wch: 18 }, { wch: 18 }, { wch: 10 }];

        // ── Sheet 2: Expense Detail ────────────────────────────────────────
        const expHeaders = [
          'Date', 'Vendor', 'Category', 'IRS Tax Category',
          `Amount (${reportCurrency})`, `Deductible (${reportCurrency})`, 'Notes',
        ];
        if (isCombined) expHeaders.push('Source Report');

        const expRows = expenseItems
          .slice()
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
          .map(e => {
            const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
            const catKey = normalizeCategory(e.category);
            const irsCategory = CATEGORY_TAX_MAP[catKey] || UNCATEGORIZED_CAT;
            const ded = deductibleAmt(irsCategory, amt);
            const row = [e.date || '', e.vendor || '', categoryLabel(e.category) || e.category || '', irsCategory, amt, ded, e.notes || ''];
            if (isCombined) row.push(e._sourceFile || '');
            return row;
          });

        const wsExpense = XLSX.utils.aoa_to_sheet([expHeaders, ...expRows]);
        const expWidths = [10, 24, 20, 30, 16, 16, 24];
        if (isCombined) expWidths.push(22);
        wsExpense['!cols'] = expWidths.map(w => ({ wch: w }));

        // ── Sheet 3: Income Detail (only if income exists) ─────────────────
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Tax Summary');
        XLSX.utils.book_append_sheet(wb, wsExpense, 'Expense Detail');

        if (incomeItems.length > 0) {
          const incHeaders = [
            'Date', 'Vendor', 'Category', 'IRS Income Category',
            `Amount (${reportCurrency})`, 'Notes',
          ];
          if (isCombined) incHeaders.push('Source Report');

          const incRows = incomeItems
            .slice()
            .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
            .map(e => {
              const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
              const incomeCatKey = normalizeCategory(e.category);
              const irsIncomeCat = INCOME_CATEGORY_TAX_MAP[incomeCatKey] || e.category || 'Other Income';
              const row = [e.date || '', e.vendor || '', categoryLabel(e.category) || e.category || '', irsIncomeCat, amt, e.notes || ''];
              if (isCombined) row.push(e._sourceFile || '');
              return row;
            });

          const wsIncome = XLSX.utils.aoa_to_sheet([incHeaders, ...incRows]);
          const incWidths = [10, 24, 22, 32, 16, 24];
          if (isCombined) incWidths.push(22);
          wsIncome['!cols'] = incWidths.map(w => ({ wch: w }));
          XLSX.utils.book_append_sheet(wb, wsIncome, 'Income Detail');
        }

        XLSX.writeFile(wb, `${filename}.xlsx`);
        resolve();
      } catch (err) {
        reject(err);
      }
    };

    if (window.XLSX) { doExport(); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    s.onload = doExport;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}
